import React, { useContext, useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    TextField,
    Switch,
    FormControlLabel,
    Stack,
    Link,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Groups as GroupsIcon,
    PersonAdd as PersonAddIcon,
    Send as SendIcon,
    Close as CloseIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { UserContextType, useAuth } from '../../auth';
import { GroupApiError, groupService } from '../../service/GroupService';
import { GroupInvitation, GroupRequest, GroupSummary } from '../../types/group';
import { groupInvitationService } from '../../service/GroupInvitationService';
import InviteGroupLeaderDialog from './InviteGroupLeaderDialog';
import RichText from '../../common/RichTextControl';
import { isBlankHtml } from '../../groups/groupFormat';
import AiRewriteButton from '../../common/AiRewriteButton';
import { useProofreadCheck } from '../../common/useProofreadCheck';

const Emitter = require('tiny-emitter/instance');

const formatDate = (value: string) =>
    new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const shorten = (text: string, max = 80) => (text.length > max ? `${text.slice(0, max).trimEnd()}…` : text);

const emptyForm: GroupRequest = { name: '', description: '', meetingInfo: '', isActive: true };

export const GroupFormDialog: React.FC<{
    open: boolean;
    group: GroupSummary | null; // null = create
    onClose: () => void;
    onSaved: () => void;
}> = ({ open, group, onClose, onSaved }) => {
    const { getToken } = useContext<UserContextType>(useAuth);
    const [form, setForm] = useState<GroupRequest>(emptyForm);
    const [saving, setSaving] = useState(false);
    // Remount the editor each time the dialog opens so it starts from that group's description.
    const [editorKey, setEditorKey] = useState(0);
    const { checkThenSave, isChecking, proofreadDialog } = useProofreadCheck('group', 'group description');

    useEffect(() => {
        if (open) {
            setForm(group
                ? { name: group.name, description: group.description, meetingInfo: group.meetingInfo ?? '', isActive: group.isActive }
                : emptyForm);
            setEditorKey((k) => k + 1);
        }
    }, [open, group]);

    const handleSave = async () => {
        if (!getToken) return;
        if (!form.name.trim() || isBlankHtml(form.description)) {
            Emitter.emit('notify', 'Please enter a name and a description.', 'error');
            return;
        }
        await checkThenSave(form.description, save);
    };

    const save = async (description: string) => {
        if (!getToken) return;
        setForm((prev) => ({ ...prev, description }));
        const request: GroupRequest = {
            name: form.name.trim(),
            description,
            meetingInfo: form.meetingInfo?.trim() || null,
            isActive: form.isActive,
        };
        setSaving(true);
        try {
            if (group) {
                await groupService.updateGroup(group.id, request, getToken());
                Emitter.emit('notify', 'Group saved.', 'success');
            } else {
                await groupService.createGroup(request, getToken());
                Emitter.emit('notify', 'Group created.', 'success');
            }
            onSaved();
            onClose();
        } catch (err) {
            Emitter.emit('notify', group ? 'Failed to save group.' : 'Failed to create group.', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>{group ? 'Edit Group' : 'Create Group'}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField
                        label="Name"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        required
                        fullWidth
                    />
                    <TextField
                        label="Meeting info"
                        placeholder="e.g. Tuesdays at 7pm in the fellowship hall"
                        value={form.meetingInfo ?? ''}
                        onChange={(e) => setForm({ ...form, meetingInfo: e.target.value })}
                        fullWidth
                    />
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                        <AiRewriteButton
                            content={form.description}
                            contentType="group"
                            isHtml
                            disabled={saving}
                            onAccept={(description) => setForm((prev) => ({ ...prev, description }))}
                        />
                    </Stack>
                    <Box sx={{ border: '1px solid #ccc', borderRadius: 1, minHeight: '200px' }}>
                        <RichText
                            key={editorKey}
                            text={form.description}
                            onChange={(html: string) => setForm((prev) => ({ ...prev, description: html }))}
                        />
                    </Box>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={!!form.isActive}
                                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                            />
                        }
                        label="Active (visible on the Groups page)"
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button onClick={handleSave} variant="contained" disabled={saving || isChecking}>
                    {isChecking ? 'Checking...' : group ? 'Save' : 'Create'}
                </Button>
            </DialogActions>
            {proofreadDialog}
        </Dialog>
    );
};

const ManageGroups: React.FC = () => {
    const navigate = useNavigate();
    const { getToken, cookies, IsAdmin } = useContext<UserContextType>(useAuth);
    const isAdmin = !!IsAdmin && IsAdmin();
    const [groups, setGroups] = useState<GroupSummary[]>([]);
    const [loading, setLoading] = useState(false);

    // Group leader invitations (Admin only)
    const [inviteOpen, setInviteOpen] = useState(false);
    const [invitations, setInvitations] = useState<GroupInvitation[]>([]);
    const [invitationsLoading, setInvitationsLoading] = useState(false);
    const [resendingId, setResendingId] = useState<number | null>(null);
    const [cancellingInvitation, setCancellingInvitation] = useState<GroupInvitation | null>(null);
    const [cancelBusy, setCancelBusy] = useState(false);

    const [formOpen, setFormOpen] = useState(false);
    const [editingGroup, setEditingGroup] = useState<GroupSummary | null>(null);
    const [deletingGroup, setDeletingGroup] = useState<GroupSummary | null>(null);

    const fetchGroups = async () => {
        if (!getToken) return;
        try {
            setLoading(true);
            setGroups(await groupService.getManaged(getToken()));
        } catch (err) {
            Emitter.emit('notify', 'Failed to load groups.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchInvitations = async () => {
        if (!getToken || !isAdmin) return;
        try {
            setInvitationsLoading(true);
            setInvitations(await groupInvitationService.getPending(getToken()));
        } catch (err) {
            Emitter.emit('notify', 'Failed to load pending invitations.', 'error');
        } finally {
            setInvitationsLoading(false);
        }
    };

    useEffect(() => {
        fetchGroups();
        fetchInvitations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cookies]);

    const handleResend = async (invitation: GroupInvitation) => {
        if (!getToken) return;
        setResendingId(invitation.id);
        try {
            await groupInvitationService.resend(invitation.id, getToken());
            Emitter.emit('notify', `Invitation resent to ${invitation.email}.`, 'success');
        } catch (err) {
            const status = err instanceof GroupApiError ? err.status : 0;
            Emitter.emit(
                'notify',
                status === 502 ? "Couldn't send the invitation email." : 'Failed to resend invitation.',
                'error'
            );
        } finally {
            setResendingId(null);
            fetchInvitations();
        }
    };

    const handleCancelInvitation = async () => {
        if (!cancellingInvitation || !getToken) return;
        setCancelBusy(true);
        try {
            await groupInvitationService.cancel(cancellingInvitation.id, getToken());
            Emitter.emit('notify', 'Invitation cancelled.', 'success');
            setCancellingInvitation(null);
        } catch (err) {
            Emitter.emit('notify', 'Failed to cancel invitation.', 'error');
        } finally {
            setCancelBusy(false);
            fetchInvitations();
        }
    };

    const handleToggleActive = async (group: GroupSummary, isActive: boolean) => {
        if (!getToken) return;
        // Optimistic update so the switch responds immediately.
        setGroups((prev) => prev.map((g) => (g.id === group.id ? { ...g, isActive } : g)));
        try {
            await groupService.setActive(group.id, isActive, getToken());
            Emitter.emit('notify', `${group.name} is now ${isActive ? 'active' : 'inactive'}.`, 'success');
        } catch (err) {
            setGroups((prev) => prev.map((g) => (g.id === group.id ? { ...g, isActive: !isActive } : g)));
            Emitter.emit('notify', 'Failed to update group status.', 'error');
        }
    };

    const handleDelete = async () => {
        if (!deletingGroup || !getToken) return;
        try {
            await groupService.deleteGroup(deletingGroup.id, getToken());
            Emitter.emit('notify', 'Group deleted.', 'success');
            setDeletingGroup(null);
            fetchGroups();
        } catch (err) {
            Emitter.emit('notify', 'Failed to delete group.', 'error');
        }
    };

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, pt: 10 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="h4">Manage Groups</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {isAdmin && (
                        <Button variant="outlined" startIcon={<PersonAddIcon />} onClick={() => setInviteOpen(true)}>
                            Invite Group Leader
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            setEditingGroup(null);
                            setFormOpen(true);
                        }}
                    >
                        Create Group
                    </Button>
                </Stack>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Meeting info</TableCell>
                            <TableCell align="center">Members</TableCell>
                            <TableCell align="center">Active</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && groups.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} align="center"><CircularProgress size={24} /></TableCell>
                            </TableRow>
                        )}
                        {!loading && groups.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    No groups yet. Use "Create Group" to start one.
                                </TableCell>
                            </TableRow>
                        )}
                        {groups.map((group) => (
                            <TableRow key={group.id} hover>
                                <TableCell>
                                    <Link component={RouterLink} to={`/admin/groups/${group.id}`} underline="hover" sx={{ fontWeight: 600 }}>
                                        {group.name}
                                    </Link>
                                </TableCell>
                                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{group.meetingInfo}</TableCell>
                                <TableCell align="center">{group.memberCount}</TableCell>
                                <TableCell align="center">
                                    <Switch
                                        checked={group.isActive}
                                        onChange={(e) => handleToggleActive(group, e.target.checked)}
                                        inputProps={{ 'aria-label': `${group.name} active` }}
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                        <Tooltip title="Members, updates & events">
                                            <IconButton onClick={() => navigate(`/admin/groups/${group.id}`)}>
                                                <GroupsIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Edit">
                                            <IconButton
                                                onClick={() => {
                                                    setEditingGroup(group);
                                                    setFormOpen(true);
                                                }}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton onClick={() => setDeletingGroup(group)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {isAdmin && (
                <Box sx={{ mt: 4 }}>
                    <Typography variant="h5" sx={{ mb: 1 }}>Pending invitations</Typography>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Email</TableCell>
                                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Invited</TableCell>
                                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Last sent</TableCell>
                                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Description</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {invitationsLoading && invitations.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center"><CircularProgress size={24} /></TableCell>
                                    </TableRow>
                                )}
                                {!invitationsLoading && invitations.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            No pending invitations. Use "Invite Group Leader" to invite someone to start a group.
                                        </TableCell>
                                    </TableRow>
                                )}
                                {invitations.map((invitation) => (
                                    <TableRow key={invitation.id} hover>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {invitation.firstName} {invitation.lastName}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', sm: 'none' } }}>
                                                {invitation.email}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{invitation.email}</TableCell>
                                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, whiteSpace: 'nowrap' }}>
                                            {formatDate(invitation.invitedAt)}
                                            {invitation.invitedByName && (
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                    by {invitation.invitedByName}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, whiteSpace: 'nowrap' }}>
                                            {formatDate(invitation.lastSentAt)}
                                        </TableCell>
                                        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                                            <Tooltip title={invitation.description}>
                                                <span>{shorten(invitation.description)}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                <Tooltip title="Resend email">
                                                    <span>
                                                        <IconButton
                                                            onClick={() => handleResend(invitation)}
                                                            disabled={resendingId === invitation.id}
                                                        >
                                                            {resendingId === invitation.id ? <CircularProgress size={20} /> : <SendIcon />}
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title="Cancel invitation">
                                                    <IconButton onClick={() => setCancellingInvitation(invitation)}>
                                                        <CloseIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}

            <InviteGroupLeaderDialog
                open={inviteOpen}
                onClose={() => setInviteOpen(false)}
                onInvited={fetchInvitations}
            />

            <Dialog open={!!cancellingInvitation} onClose={() => !cancelBusy && setCancellingInvitation(null)}>
                <DialogTitle>Cancel invitation?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        The setup link sent to <strong>{cancellingInvitation?.firstName} {cancellingInvitation?.lastName}</strong> will
                        stop working. If they don't lead any other group, they'll also lose the Group Admin role.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCancellingInvitation(null)} disabled={cancelBusy}>Keep</Button>
                    <Button onClick={handleCancelInvitation} color="error" variant="contained" disabled={cancelBusy}>
                        Cancel Invitation
                    </Button>
                </DialogActions>
            </Dialog>

            <GroupFormDialog
                open={formOpen}
                group={editingGroup}
                onClose={() => setFormOpen(false)}
                onSaved={fetchGroups}
            />

            <Dialog open={!!deletingGroup} onClose={() => setDeletingGroup(null)}>
                <DialogTitle>Delete group?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This permanently deletes <strong>{deletingGroup?.name}</strong> along with its members, updates and events.
                        To hide it temporarily, turn off Active instead.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeletingGroup(null)}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ManageGroups;
